import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {BehaviorSubject, combineLatest, defer, finalize, shareReplay, Subject, takeUntil, tap} from 'rxjs';
import {MenuItem} from 'primeng/api';
import {downloadInvoice as downloadInvoiceHelper} from '../../../sdk/util/invoice-download.helper';
import {handleLoadError} from '../../../sdk/util/load-error.util';
import {
    Account,
    AccountPage,
    AccountSearchCriteria,
    AccountsSortField,
    BillingType,
    BusinessDetails,
    BusinessECommerceStore,
    BusinessECommerceStoreInput,
    EcommerceDashboardPeriod,
    ECommerceStore,
    EcommerceUnsettledTotals,
    WebAccountService,
    WebBusinessService,
    WebEcommerceDashboardService
} from 'web-angular-client';
import {formatDate} from '@angular/common';
import {ROUTES} from '../../../app.routes';
import {AlertService} from '../../../sdk/service/alert.service';
import {ConfirmationService} from '../../../sdk/service/confirmation.service';
import {AuthenticationService} from '../../../sdk/service/authentication.service';
import {EnvironmentService} from '../../../sdk/service/environment.service';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {DropdownItem} from '../../../sdk/interface/data/DropdownItem';
import {ECOMMERCE_PERIOD_OPTIONS} from '../ecommerce-period-options';
import {catchError, map, switchMap} from 'rxjs/operators';
import {browserTimezone} from '../../../sdk/component/search/ecommerce-account-search.service';
import {SyncValidators} from '../../../sdk/validator/SyncValidators';
import {requireWhileSelected} from '../../../sdk/validator/require-while-selected';
import {RefundDialogService} from '../../../sdk/service/refund-dialog.service';
import {isRefundEligible} from '../../../sdk/util/refund-eligibility';
import {copyToClipboard} from '../../../sdk/util/clipboard';
import {LayoutService} from '../../layout/layout.service';
import {ECommerceStoreVerificationDisplay} from '../../../sdk/interface/ecommerce-store-verification-display';
import {LivePaymentsBanner} from '../../../sdk/interface/live-payments-banner';
import {
    buildLivePaymentsBanner,
    getECommerceStoreVerification
} from '../../../sdk/util/ecommerce/ecommerce-store-verification';

@Component({
    selector: 'app-ecommerce-dashboard',
    templateUrl: './ecommerce-dashboard.component.html',
    styleUrls: ['./ecommerce-dashboard.component.scss'],
    standalone: false
})
export class EcommerceDashboardComponent implements OnInit, OnDestroy
{
    private static readonly RECENT_LIMIT = 5;

    /** Seeds the table's sort indicator to match the order the card is actually loaded in. */
    readonly defaultSortField: string = AccountsSortField.DATE_TIME_CREATED;

    readonly defaultSortOrder = 'DESC' as const;

    readonly settlementListPath = ROUTES.SECURE.SETTLEMENT_BATCH.LIST.path;
    readonly ecommerceListPath = ROUTES.SECURE.ACCOUNT.ECOMMERCE.path;
    readonly accountSettingsPath = ROUTES.SECURE.BUSINESS.EDIT.path;

    readonly periodOptions: DropdownItem[] = ECOMMERCE_PERIOD_OPTIONS;

    readonly selectedStoreId$ = new BehaviorSubject<number | null>(null);
    readonly selectedPeriod$ = new BehaviorSubject<EcommerceDashboardPeriod>(EcommerceDashboardPeriod.LAST_7_D);
    readonly balanceLoading$ = new BehaviorSubject<boolean>(false);
    readonly statsLoading$ = new BehaviorSubject<boolean>(false);
    readonly recentLoading$ = new BehaviorSubject<boolean>(false);
    readonly recentError$ = new BehaviorSubject<boolean>(false);
    storeOptions: DropdownItem[] = [{label: 'All Stores', value: null}];
    selectedStore: BusinessECommerceStore | null = null;
    publicKeyVisible = false;
    secretKeyVisible = false;
    copiedField: 'public' | 'secret' | 'externalIdentifier' | null = null;
    externalIdentifier: string | undefined;
    externalIdentifierUnavailable = false;
    storesById = new Map<number, BusinessECommerceStore>();
    hasStores = false;
    availableStoresToAdd: ECommerceStore[] = [];
    addingStore = false;
    readonly addStoreForm = new FormGroup({
        store: new FormControl<ECommerceStore | null>(null, Validators.required),
        domain: new FormControl<string>('', [SyncValidators.validateDomain()]),
        description: new FormControl<string>('', Validators.maxLength(255))
    });
    /**
     * Whether this user may see and manage a store's API credentials.
     * <p>
     * Read from the permission the server gates these endpoints on rather than from a role list, so granting or
     * revoking it cannot leave the screen and the controller disagreeing. Resolved once on init, because the
     * signed-in user's permissions do not change while the screen is open.
     */
    canManageECommerceStoreCredentials = false;
    /** Whether this user may open account settings, so the store settings link is not a dead end for invited users. */
    canOpenStoreSettings = false;
    private readonly destroy$ = new Subject<void>();
    readonly stats$ = combineLatest([
        this.selectedStoreId$,
        this.selectedPeriod$
    ]).pipe(
        tap(() => this.statsLoading$.next(true)),
        switchMap(([storeId, period]) =>
            this.webEcommerceDashboardService.getEcommerceDashboardStats(
                period,
                storeId ?? undefined,
                browserTimezone()
            ).pipe(
                finalize(() => this.statsLoading$.next(false)),
                catchError((err) =>
                    handleLoadError(err, undefined, 'Failed to load dashboard stats.', null, this.alertService))
            )
        ),
        takeUntil(this.destroy$),
        shareReplay(1)
    );
    readonly averageOrderValue$ = this.stats$.pipe(
        map(stats => stats?.successCount ? stats.totalSales / stats.successCount : 0)
    );
    readonly conversionRatePercent$ = this.stats$.pipe(
        map(stats => stats?.transactionVolume
            ? (stats.successCount / stats.transactionVolume) * 100
            : 0
        )
    );
    readonly balanceSummary$ = defer(() =>
    {
        this.balanceLoading$.next(true);
        return this.webEcommerceDashboardService.getEcommerceDashboardBalanceSummary();
    }).pipe(
        finalize(() => this.balanceLoading$.next(false)),
        catchError((err) =>
            handleLoadError(err, undefined, 'Failed to load balance summary.', null, this.alertService)),
        takeUntil(this.destroy$),
        shareReplay(1)
    );
    /**
     * The card's row set: the five most recent for the selected store. Keyed off the store filter only — the table
     * sorts these rows itself, so sorting reorders the five rather than changing which five they are.
     */
    readonly recentTransactions$ = this.selectedStoreId$.pipe(
        tap(() =>
        {
            this.recentLoading$.next(true);
            this.recentError$.next(false);
        }),
        switchMap(storeId =>
            this.webAccountService.searchEcommerceAccounts(
                0,
                EcommerceDashboardComponent.RECENT_LIMIT,
                this.defaultSortField,
                this.defaultSortOrder,
                undefined,
                browserTimezone(),
                this.buildRecentSearchCriteria(storeId)
            ).pipe(
                finalize(() => this.recentLoading$.next(false)),
                map((page: AccountPage) => page?.content ?? []),
                catchError((err) =>
                    handleLoadError(
                        err,
                        this.recentError$,
                        'Failed to load recent transactions.',
                        [],
                        this.alertService))
            )
        ),
        takeUntil(this.destroy$),
        shareReplay(1)
    );

    constructor(
        private readonly router: Router,
        private readonly webBusinessService: WebBusinessService,
        private readonly webAccountService: WebAccountService,
        private readonly webEcommerceDashboardService: WebEcommerceDashboardService,
        private readonly alertService: AlertService,
        private readonly confirmationService: ConfirmationService,
        private readonly authenticationService: AuthenticationService,
        private readonly ngxLoader: NgxUiLoaderService,
        private readonly refundDialogService: RefundDialogService,
        readonly environmentService: EnvironmentService,
        private readonly layoutService: LayoutService)
    {
    }

    ngOnInit(): void
    {
        this.canManageECommerceStoreCredentials =
            this.authenticationService.hasPrivilege('ECOMMERCE_STORE_MANAGE');
        this.canOpenStoreSettings = this.authenticationService.hasPrivilege(ROUTES.SECURE.BUSINESS.EDIT.privilege);
        this.requireDomainOnceStoreSelected();
        this.loadStores();
        this.loadExternalIdentifier();
    }

    /**
     * Makes the storefront domain required only while a store is selected.
     * <p>
     * The domain validator alone passes an empty value, so without this the form stays valid with the domain blank,
     * "Add store" stays enabled, and the engine refuses the call.
     */
    private requireDomainOnceStoreSelected(): void
    {
        requireWhileSelected(
            this.addStoreForm.controls.store,
            this.addStoreForm.controls.domain,
            takeUntil(this.destroy$));
    }

    /** The support portal a rejected merchant is sent to, matching the Get Help link in the side menu. */
    get supportPortalUrl(): string
    {
        return this.environmentService.supportPortalUrl;
    }

    /**
     * The badge for the selected store's compliance check, or null when no single store is selected.
     *
     * @return {ECommerceStoreVerificationDisplay | null} the badge to render, or null
     */
    get storeVerification(): ECommerceStoreVerificationDisplay | null
    {
        return this.selectedStore ? getECommerceStoreVerification(this.selectedStore) : null;
    }

    /**
     * The live-payments banner, which changes when the selected store has not passed its check. Without this the
     * banner tells a merchant to switch to production while the gateway refuses every payment they take.
     *
     * @return {LivePaymentsBanner} the banner to render
     */
    get livePaymentsBanner(): LivePaymentsBanner
    {
        return buildLivePaymentsBanner(this.selectedStore, this.environmentService.domainVerificationSla);
    }

    /** The setup guide's setup steps, linked from the "Sandbox and live payments setup" banner. */
    get setupGuideLink(): string
    {
        return this.environmentService.setupGuideLink + '#setup';
    }

    /**
     * Reads the External Identifier from the business details already loaded by the shared {@link LayoutService}
     * (the app shell fetches them once), and again whenever the layout re-emits, so the dashboard does not issue its
     * own duplicate business-details request. A failed load with no cached identifier marks it unavailable, so the
     * banner shows the refresh hint instead of silently dropping the identifier.
     */
    private loadExternalIdentifier(): void
    {
        this.externalIdentifier = this.layoutService.businessDetails?.externalIdentifier;
        this.externalIdentifierUnavailable = this.layoutService.businessDetailsLoadFailed && !this.externalIdentifier;
        this.layoutService.load.pipe(takeUntil(this.destroy$)).subscribe((businessDetails: BusinessDetails | null) =>
        {
            this.externalIdentifier = businessDetails?.externalIdentifier;
            this.externalIdentifierUnavailable = false;
        });
        this.layoutService.loadFailed.pipe(takeUntil(this.destroy$)).subscribe(() =>
            this.externalIdentifierUnavailable = !this.externalIdentifier);
    }

    ngOnDestroy(): void
    {
        this.destroy$.next();
        this.destroy$.complete();
    }

    togglePublicKey(): void
    {
        this.publicKeyVisible = !this.publicKeyVisible;
    }

    toggleSecretKey(): void
    {
        this.secretKeyVisible = !this.secretKeyVisible;
    }

    /** Public key shown in the credentials panel: full when revealed, truncated when hidden, '-' when absent. */
    getDisplayedPublicKey(): string
    {
        const publicKey = this.selectedStore?.publicKey;
        if (!publicKey)
        {
            return '-';
        }
        return this.publicKeyVisible ? publicKey : publicKey.slice(0, 12) + '...';
    }

    onStoreChange(storeId: number | null): void
    {
        this.selectedStoreId$.next(storeId ?? null);
        this.selectedStore = storeId !== null && storeId !== undefined
            ? (this.storesById.get(storeId) ?? null)
            : null;
        this.publicKeyVisible = false;
        this.secretKeyVisible = false;
    }

    onPeriodChange(period: EcommerceDashboardPeriod): void
    {
        if (period)
        {
            this.selectedPeriod$.next(period);
        }
    }

    /**
     * Arrow-function so `this` stays bound when passed as a callback to the shared table component. Rebuilds the
     * context-menu items for the row whose menu is being opened — needed so the Invoice item's `disabled` state
     * tracks the current row's billing type.
     */
    readonly buildMenuItemsForAccount = (account: Account): MenuItem[] =>
    {
        const items: MenuItem[] = [
            {
                label: 'View',
                icon: 'fa fa-eye fa-lg',
                command: () => this.viewAccount(account)
            },
            {
                label: 'Invoice',
                icon: 'fa fa-download fa-lg',
                command: () => this.downloadInvoice(account),
                disabled: !this.isInvoiceBillingType(account?.billingType)
            }
        ];
        if (isRefundEligible(account))
        {
            items.push({
                label: 'Refund',
                icon: 'fa fa-undo fa-lg',
                command: () => this.refundDialogService.open(account)
            });
        }
        return items;
    };

    copyKey(field: 'public' | 'secret'): void
    {
        this.copyWithIndicator(field, field === 'public' ? this.selectedStore?.publicKey : this.selectedStore?.secretKey);
    }

    copyExternalIdentifier(): void
    {
        this.copyWithIndicator('externalIdentifier', this.externalIdentifier);
    }

    /** Copies the value and briefly flags the field as copied, alerting when the clipboard is unavailable. */
    private copyWithIndicator(field: 'public' | 'secret' | 'externalIdentifier', value: string | undefined): void
    {
        if (!value)
        {
            return;
        }
        copyToClipboard(value)
            .then(() =>
            {
                this.copiedField = field;
                setTimeout(() =>
                {
                    if (this.copiedField === field)
                    {
                        this.copiedField = null;
                    }
                }, 3000);
            })
            .catch(() =>
            {
                // Clipboard API can reject when unavailable, permission is denied, on non-HTTPS contexts, or in
                // unsupported browsers.
                this.alertService.error('Failed to copy to clipboard.');
            });
    }

    getAmount(account: Account): number
    {
        return account?.dateTimePaid ? account.amountPaid : account.amount;
    }

    /**
     * Formats a settlement date (ISO yyyy-MM-dd from the backend) for the balance bar's "Settles on {date}"
     * descriptions. Day of week is intentionally omitted — settlements aren't automated, so naming the day
     * implies a hard guarantee we don't make. Empty string when the date is missing so the template renders a
     * neutral placeholder.
     */
    formatSettlementDate(date: string | null | undefined): string
    {
        if (!date)
        {
            return '';
        }
        return formatDate(date, 'd MMM yyyy', 'en');
    }

    /**
     * Tooltip copy for the AWAITING SETTLEMENT card. NET merchants see the rolling pipeline as a net figure
     * (fees already deducted across every batch); GROSS merchants see it as a gross figure with fees invoiced
     * separately. Empty-state copy nudges the merchant toward end-of-day batch closure so a quiet "0" doesn't
     * look like the feature is broken.
     */
    awaitingSettlementTooltip(balance: EcommerceUnsettledTotals | null | undefined): string
    {
        if (balance?.latestExpectedSettlementDate == null)
        {
            return 'You\'ll see your rolling settlement total here.';
        }
        return this.isGrossSettlement(balance)
            ? 'Total gross amount across every batch awaiting settlement. Fees are billed separately on your monthly invoice, not deducted from this figure.'
            : 'Total amount across every batch awaiting settlement. Each batch\'s fees have already been deducted - this is what will land in your bank, summed across all pending batches.';
    }

    /**
     * Tooltip copy for the AVAILABLE FOR SETTLEMENT card. Same NET-vs-GROSS distinction as
     * {@link awaitingSettlementTooltip}, but scoped to the next-to-settle batch only.
     */
    availableForSettlementTooltip(balance: EcommerceUnsettledTotals | null | undefined): string
    {
        if (balance?.expectedSettlementDate == null)
        {
            return 'You\'ll see your next settlement here once your current batch closes. Batches only close at end-of-day if the requirements are met.';
        }
        return this.isGrossSettlement(balance)
            ? 'This is the gross amount that will be paid into your bank account. Fees are billed separately on your monthly invoice.'
            : 'This is the amount that will be paid into your bank account. The fees for this batch are already deducted.';
    }

    /**
     * Tooltip copy for the FEES & DEDUCTIONS card. This is the load-bearing one — NET merchants might think the
     * fees are about to come out (they're already gone); GROSS merchants might think they're deducted from the
     * settlement (they aren't). Be explicit about which side of the deduction the merchant is on.
     */
    feesAndDeductionsTooltip(balance: EcommerceUnsettledTotals | null | undefined): string
    {
        if (balance?.expectedSettlementDate == null)
        {
            return 'Fees for your next settlement show here once your current batch closes.';
        }
        return this.isGrossSettlement(balance)
            ? 'Transaction and settlement fees for your next settlement. Not deducted from it, billed on your monthly invoice.'
            : 'Transaction and settlement fees for your next settlement, already deducted from Available for Settlement.';
    }

    /** Short description under the FEES & DEDUCTIONS amount — mirrors the tooltip's NET-vs-GROSS branch. */
    feesAndDeductionsDescription(balance: EcommerceUnsettledTotals | null | undefined): string
    {
        if (balance?.expectedSettlementDate == null)
        {
            return 'No fees pending';
        }
        return this.isGrossSettlement(balance) ? 'Billed on your next invoice' : 'Already deducted';
    }

    getOrderNumber(account: Account): string
    {
        return account?.description || '-';
    }

    getStoreLabel(account: Account): string
    {
        if (!account)
        {
            return '-';
        }
        const storeId = account.eCommerceStoreId ?? null;
        if (storeId != null)
        {
            const matched = this.storesById.get(storeId);
            if (matched?.eCommerceStoreName)
            {
                return matched.eCommerceStoreName;
            }
        }
        return account.eCommerceStoreName ?? (storeId == null ? '-' : `${storeId}`);
    }

    isGuestAdminUser(): boolean
    {
        return this.authenticationService.isGuestAdminUser();
    }

    isInvoiceBillingType(billingType: BillingType | null | undefined): boolean
    {
        return billingType === null || billingType === undefined || billingType === BillingType.INVOICE;
    }

    viewAccount(account: Account | null): void
    {
        if (!account?.id)
        {
            return;
        }
        if (this.isGuestAdminUser())
        {
            this.alertService.error('Guest accounts cannot perform this action.', false);
            return;
        }
        this.router.navigate([ROUTES.SECURE.ACCOUNT.VIEW.pathSansParameters, account.id]).then();
    }

    downloadInvoice(account: Account | null): void
    {
        if (!account?.id)
        {
            return;
        }
        if (this.isGuestAdminUser())
        {
            this.alertService.error('Guest accounts cannot perform this action.', false);
            return;
        }
        downloadInvoiceHelper(account.id, {
            invoiceLoader: id => this.webAccountService.getInvoiceByAccountId(id),
            onStart: () => this.ngxLoader.start(),
            onComplete: () => this.ngxLoader.stop(),
            onError: message => this.alertService.error(message)
        });
    }

    /** Links the selected store type to the business, mirroring the Account Settings add-store flow. */
    addStore(): void
    {
        const store = this.addStoreForm.controls.store.value;
        const domain = this.addStoreForm.controls.domain.value?.trim();
        const description = this.addStoreForm.controls.description.value?.trim() || undefined;
        if (!store || this.addStoreForm.invalid)
        {
            return;
        }

        const confirmDetail = domain
            ? `Are you sure you want to add ${store.storeName} at ${domain} as one of your e-commerce stores?`
            : `Are you sure you want to add ${store.storeName} as one of your e-commerce stores?`;

        this.confirmationService.confirm(`Add ${store.storeName} store`, confirmDetail)
            .then((confirmed) =>
            {
                if (!confirmed)
                {
                    return;
                }

                const input: BusinessECommerceStoreInput = {
                    eCommerceStoreId: store.id,
                    eCommerceStoreName: store.storeName,
                    domain: domain,
                    description: description
                };

                this.addingStore = true;
                this.webBusinessService.addECommerceStoreToBusiness(input)
                    .pipe(finalize(() => this.addingStore = false), takeUntil(this.destroy$))
                    .subscribe({
                        next: () =>
                        {
                            this.alertService.success(
                                domain
                                    ? `Successfully added ${store.storeName} e-commerce store at ${domain} to your business.`
                                    : `Successfully added ${store.storeName} e-commerce store to your business.`);
                            this.addStoreForm.reset({store: null, domain: '', description: ''});
                            this.loadStores();
                        },
                        error: () =>
                        {
                            this.alertService.error(
                                'Failed to add e-commerce store. Please try again or contact support.');
                        }
                    });
            });
    }

    /** Re-binds selectedStore to the freshly-loaded object (keys/name may have changed) or clears it if it's gone. */
    private refreshSelectedStore(): void
    {
        const currentId = this.selectedStoreId$.value;
        this.selectedStore = currentId == null ? null : (this.storesById.get(currentId) ?? null);
    }

    private isGrossSettlement(balance: EcommerceUnsettledTotals | null | undefined): boolean
    {
        return balance?.settlementType === EcommerceUnsettledTotals.SettlementTypeEnum.GROSS;
    }

    /**
     * Builds the search criteria for the Recent Transactions card — the top-5-most-recent ecommerce accounts for
     * the optionally-selected store. {@code accountType} isn't set here because the endpoint
     * ({@code searchEcommerceAccounts}) already scopes server-side to {@code ECOMMERCE_RTP}. The card is
     * deliberately NOT scoped by the dashboard's period selector — the merchant always wants to see their most
     * recent activity at a glance, regardless of which window the stats / balance bar are viewing.
     */
    private buildRecentSearchCriteria(storeId: number | null): AccountSearchCriteria
    {
        const criteria: AccountSearchCriteria = {};
        if (storeId !== null && storeId !== undefined)
        {
            criteria.businessECommerceStoreIds = [storeId];
        }
        return criteria;
    }

    private loadStores(): void
    {
        this.webBusinessService.getECommerceStoresForBusiness().pipe(takeUntil(this.destroy$)).subscribe({
            next: (stores: BusinessECommerceStore[]) =>
            {
                const list = stores ?? [];
                this.storesById.clear();
                list.forEach((store) =>
                {
                    if (store.id !== null && store.id !== undefined)
                    {
                        this.storesById.set(store.id, store);
                    }
                });
                this.storeOptions = this.getStoreOptions(list);
                this.hasStores = this.storesById.size > 0;
                this.autoSelectSingleActiveStore(list);
                this.refreshSelectedStore();

                if (!this.hasStores)
                {
                    this.loadAvailableStoresToAdd(list);
                }
            },
            error: () =>
            {
                this.alertService.error('Failed to load e-commerce stores.');
            }
        });
    }

    /** Selects the store automatically when the merchant has exactly one active store, so there's nothing to pick. */
    private autoSelectSingleActiveStore(stores: BusinessECommerceStore[]): void
    {
        const activeStores = stores.filter((store) => store.active === true);
        if (activeStores.length === 1)
        {
            const storeId = activeStores[0].id;
            if (storeId !== null && storeId !== undefined)
            {
                this.onStoreChange(storeId);
            }
        }
    }

    /** Loads the store types the merchant can still add (the full list minus the ones already linked). */
    private loadAvailableStoresToAdd(assignedStores: BusinessECommerceStore[]): void
    {
        const assignedIds = new Set(assignedStores.map((store) => store.eCommerceStoreId ?? store.id));
        this.webBusinessService.getECommerceStoreList().pipe(takeUntil(this.destroy$)).subscribe({
            next: (stores: ECommerceStore[]) =>
            {
                this.availableStoresToAdd = (stores ?? []).filter((store) => !assignedIds.has(store.id));
            },
            error: () =>
            {
                this.alertService.error('Failed to load available e-commerce stores.');
            }
        });
    }

    /** Builds the store dropdown options — an "All stores" entry plus one entry per linked store. */
    private getStoreOptions(stores: BusinessECommerceStore[]): DropdownItem[]
    {
        return [
            {label: 'All Stores', value: null},
            ...stores
                .map((store) =>
                {
                    const name = store.eCommerceStoreName ?? `${store.id}`;
                    const label = store.domain ? `${store.domain} – ${name}` : name;
                    return {label, value: store.id};
                })
                .filter((option) => option.value !== null && option.value !== undefined)
        ];
    }

}
